import { queryOptions } from '@tanstack/react-query'

import { KompisDefaultFields } from '@inter-ikea-kompis/services'
import type { IDexfSettings } from '@inter-ikea-kompis/types'

import { getApplicationProducts } from '@sofa-platform/dexf-utils'
import rangeDataRecord from '@sofa-platform/range-data-generator/range-data'
import type { sofaTypes } from '@sofa-platform/range-data-generator/type-object'
import type { Enriched, EnrichedRangeDataRecord, ProductData, RangeDataRecord } from '@sofa-platform/types'

type RangeName = keyof typeof sofaTypes

import { APP_ID, DEXF_API_KEY } from '@/integrations/kompis/constants'
import { getLocaleFromPath, isNAMarket } from '@/utils/base-path-params'

const NA_BLOCKED_RANGES: RangeName[] = ['landskrona', 'vimle']

export function rangeDataQueryOptions(dexfAppSettings: IDexfSettings | undefined, isDev?: boolean) {
  return queryOptions({
    enabled: !!dexfAppSettings,
    queryFn: () => fetchRangeData(dexfAppSettings, isDev),
    queryKey: ['range-data', getLocaleFromPath()],
    staleTime: Infinity,
  })
}

function enrichData<T extends { itemNo: string }>(data: null | T[], allProducts: ProductData[]): Enriched<T>[] | null {
  return (
    data
      ?.map((item) => {
        const productData = allProducts.find((product) => product.itemId.includes(item.itemNo))
        if (!productData) return null
        return { ...item, productData }
      })
      .filter(Boolean) ?? null
  )
}
async function fetchRangeData(
  dexfAppSettings: IDexfSettings | undefined,
  isDev?: boolean,
): Promise<EnrichedRangeDataRecord> {
  if (!dexfAppSettings) return {}

  const rangeProductData = await getApplicationProducts({
    appId: APP_ID,
    basePath: dexfAppSettings.kompis.endpoints.dexfWebplannerService,
    dexfApiKey: DEXF_API_KEY,
    fields: [...Object.values(KompisDefaultFields), 'validDesignPart', 'filterAttribute'],
  })

  /**
   * Enrich sofas, legs, mattresses and coverItems with product data for the current market
   */
  const enrichedData: EnrichedRangeDataRecord = {}

  const data = Object.entries(rangeDataRecord as unknown as RangeDataRecord)

  for (const [rangeId, value] of data) {
    if (isNAMarket() && NA_BLOCKED_RANGES.includes(rangeId as RangeName)) continue

    const sofas = value.sofas
      .map((sofa) => {
        const productData = rangeProductData.find((product) => product.itemId.includes(sofa.itemNo))
        if (!productData || !sofa.code) return null

        if (!isDev && sofa.isMissingBryggaData) return null
        return { ...sofa, productData }
      })
      .filter(Boolean)

    if (sofas.length === 0) continue

    const legs = enrichData(value.legs, rangeProductData)
    const mattresses = enrichData(value.mattresses, rangeProductData)
    const coverProducts = enrichData(value.coverProducts, rangeProductData)

    enrichedData[rangeId] = { ...value, coverProducts, legs, mattresses, sofas }
  }

  return enrichedData
}
